import type { ProximityTier } from "@homero/pricing";
import type { Notice } from "src/components/utils/PriceNotice";
import { CLEANING_TYPE_PRICE } from "src/constants/pricing";
import {
  addOnsCostDollars,
  computeBookingPriceDollars,
  isAddonFree as typeIncludesFreeExtras,
  normalizeTypesParam,
} from "src/lib/price";
import { getQueryParam } from "src/weirdStuff/state";

// Any cleaning type the shared package can resolve is accepted, then
// normalised to its id — the store holds the type, never its price. The old exact-string match against a
// hardcoded table silently discarded every selection made on the landing
// page once that page's copy of the table fell behind.
const initialTypes = normalizeTypesParam(
  // Accept anything; normalizeTypesParam resolves it or falls back to Standard.
  getQueryParam("t", "standard", () => true)
);

/**
 * Whether the starting type comes with its extras included — Super Clean and
 * Move In / Out. Those types are one-off jobs and are never offered a plan, so
 * they start as One Time with the extras in, exactly as `set_types` sets them
 * when picked on step one. A type from a landing-page link skipped `set_types`,
 * and kept the plan default with no frequency choice shown to undo it.
 */
const initialFreeExtras = typeIncludesFreeExtras(initialTypes);

/**
 * The ZIP the landing page's hero already checked, from `?zip=`. Only a
 * well-formed one is taken; whether we clean there is asked again here, since a
 * link can be old or hand-typed.
 */
const initialZip = getQueryParam("zip", "", (value) => /^\d{5}$/.test(value));

export let initialState = {
  page: 1,
  user: {
    token: undefined,
    credits: 0,
  },
  bedrooms: getQueryParam(
    "be",
    "0",
    (value) => Number(value) >= 0 && Number(value) <= 5
  ),
  bathrooms: getQueryParam(
    "ba",
    "1",
    (value) => Number(value) >= 1 && Number(value) <= 5
  ),
  types: initialTypes as string,
  isFlexible: true,
  totalPrice: 0,
  subTotal: Number(CLEANING_TYPE_PRICE.Standard),
  appointmentValue: Number(CLEANING_TYPE_PRICE.Standard),
  cleanDate: "",
  cleanTime: "",
  /**
   * Whether the customer took Same-Day, and which arrival window.
   *
   * Both are recorded choices rather than derived state: nothing in this app can
   * work out whether Same-Day is *available* — that is the server's answer (ADR
   * 0004) — so what is kept here is only what the customer picked from what they
   * were offered. `sameDaySurchargePercent` is the figure the server returned for
   * that date, held so the displayed total can include it.
   */
  sameDay: false,
  /**
   * Whether the date step is showing Same-Day rather than the calendar and the
   * fixed-time grid. Distinct from `sameDay`, which means a window has actually
   * been chosen: between entering the mode and picking a window the customer has
   * a date but no time, and the step has to know to keep showing them windows.
   */
  sameDayMode: false,
  arrivalWindow: "",
  /**
   * The window's customer-facing label, e.g. "12:00-3:00pm", alongside its id.
   * Kept because every human-readable surface needs it and the id is not one —
   * see `setSameDay`.
   */
  arrivalWindowLabel: "",
  sameDaySurchargePercent: 0,
  /**
   * The plan the customer had before entering Same-Day, held so leaving the mode
   * gives it back.
   *
   * Same-Day is One Time only (see `enter_same_day`), so entering the mode has to
   * move `occurance` to 0. Without somewhere to put the old value, a customer who
   * looked at today's windows and went back to the calendar would silently have
   * lost the plan they picked — the frequency row is above the fold on the way in
   * and unchanged on the way out, so nothing on screen would tell them.
   *
   * Null whenever we are not inside the mode.
   */
  frequencyBeforeSameDay: null as string | number | null,
  discount: 0,
  occurance: initialFreeExtras ? 0 : 15,
  address: "",
  /**
   * The ZIP the address checker pinned, kept beside the address rather than
   * appended to it.
   *
   * `StepThree` used to dispatch `setAddress(address + ", " + zip)`, which made
   * the ZIP a substring of a free-text field — fine for printing on an email,
   * useless for the query proximity pricing runs, and impossible to correct
   * without re-parsing prose. Empty when the checker could not pin one.
   *
   * Starts as the landing page's ZIP when the customer came from its hero.
   */
  zip: initialZip,
  /**
   * The customer arrived with a ZIP, so step 1 hides its field and the ZIP is
   * shown, and changed, from the chip in the header (`ZipChip`). Fixed for the
   * visit: a customer who clears the ZIP there still edits it there.
   */
  zipFromLink: initialZip !== "",
  /** Whether the header chip's ZIP editor is open. */
  zipEditorOpen: false,
  /**
   * The area-pricing tier the server quoted for this ZIP, date and arrival, and
   * the token that lets checkout charge exactly that. `proximityFor` is the
   * question it answered (`quoteKey`), so an answer for an old date is never
   * read as one for the new date while the new request is in flight.
   *
   * Applies to one-time cleanings only; `computeBookingPrice` checks.
   */
  proximityTier: "none" as ProximityTier,
  proximityPercent: 0,
  proximityToken: null as string | null,
  proximityFor: "",
  /** Whether the quote for `proximityFor` is still on its way. */
  proximityPending: false,
  /**
   * Why the price moved after the customer was shown it, and what it was, for
   * the notice at checkout. Null when nothing moved.
   */
  priceNotice: null as Notice | null,
  appartmentNo: "",
  addOns: {
    type: (initialFreeExtras
      ? ["Inside Oven", "Inside Refrigerator", "Inside Cabinets"]
      : []) as string[],
    cost: 0,
  },
  getInType: {
    type: "Someone in home",
    text: "",
  },
  pets: {
    type: "No Pets",
    text: "",
  },
  instructions: "",
  tip: 0,
  code: "",
  codeDiscount: 0,
  paymentIntent: null,
  taskPaymentIntent: null,
};

/**
 * Dropping the Same-Day *selection*: no window, no surcharge, not a Same-Day
 * booking.
 *
 * Named once because seven cases clear it — every action that changes something
 * Same-Day depends on. Written out at each of them, adding `arrivalWindowLabel`
 * meant seven hand-edits, and the next field will mean seven more; the one that
 * gets missed leaves a stale label or a surcharge on a booking that is no longer
 * Same-Day, which is a wrong price rather than a wrong render.
 */
const CLEARED_SELECTION = {
  sameDay: false,
  arrivalWindow: "",
  arrivalWindowLabel: "",
  sameDaySurchargePercent: 0,
} as const;

/** The selection, plus leaving the Same-Day step itself. */
const CLEARED_SAME_DAY = { ...CLEARED_SELECTION, sameDayMode: false } as const;

export const bookingReducer = (
  state = initialState,
  action: { type: string; payload?: number | string | undefined | Object | any }
) => {
  switch (action.type) {
    case "set_page":
      return {
        ...state,
        page: action.payload,
        // The notice explains a total at checkout. Going back to change
        // something makes it stale, so it goes; step 3 sets the ZIP one on its
        // way to checkout, which keeps it.
        priceNotice: action.payload >= 4 ? state.priceNotice : null,
      };
    case "set_bedrooms":
      return {
        ...state,
        bedrooms: action.payload,
      };
    case "set_bathrooms":
      return {
        ...state,
        bathrooms: action.payload,
      };
    case "set_types": {
      // Decided by cleaning *type*, not by comparing a truncated price against
      // a threshold. `parseInt("273.45") >= 273.45` is false, which silently
      // voids the free-extras rule and charges the customer up to $105 extra
      // with no error anywhere.
      const includesFreeExtras = typeIncludesFreeExtras(action.payload);

      return {
        ...state,
        types: action.payload,
        addOns: {
          type: includesFreeExtras
            ? ["Inside Oven", "Inside Refrigerator", "Inside Cabinets"]
            : [],
          cost: 0,
        },
        /**
         * `sameDayMode` survives a type change (see below), and Same-Day is One
         * Time only — so the plan default must not be handed back underneath a
         * customer standing in a mode with no frequency row to undo it. The
         * remembered plan is updated instead, so leaving the mode gives them the
         * default for the type they now have rather than the one they left.
         */
        occurance: state.sameDayMode
          ? 0
          : includesFreeExtras || state.codeDiscount > 0
          ? 0
          : 15,
        frequencyBeforeSameDay: state.sameDayMode
          ? includesFreeExtras || state.codeDiscount > 0
            ? 0
            : 15
          : state.frequencyBeforeSameDay,
        /**
         * The Same-Day choice goes, for the same reason `set_date` drops it.
         *
         * The surcharge is configured per cleaning type *and* per day —
         * `sameDaySurchargePercent[cleaningType][dayOfWeek]` — so a percentage
         * fetched for Standard says nothing about Special Mission. It was held
         * here unchanged across a type change, which broke in both directions:
         * switching from a surcharged type to an unsurcharged one billed a
         * surcharge the server would not charge, and switching the other way
         * showed no surcharge while the server charged one. The second is the
         * "Invalid total amount" failure, reached from a new direction.
         *
         * The percentage cannot simply be refreshed here — only the server knows
         * it, and asking is a round trip this reducer cannot make. So the choice
         * that carried it is dropped and the customer re-picks a window, priced
         * for the type they actually chose.
         *
         * `sameDayMode` deliberately stays: they asked to book today, and that is
         * still true. They land back on the window list with nothing selected.
         */
        ...CLEARED_SELECTION,
      };
    }
    case "get_total_price": {
      // Computed by @homero/pricing, the same code the server uses. Previously
      // this reimplemented the math and rounded in a different place than
      // hooks/usePrice.ts, so the subtotal in the sidebar could differ by a
      // dollar from the total posted at checkout.
      const price = computeBookingPriceDollars(state);
      return {
        ...state,
        subTotal: price.subTotal,
        totalPrice: price.totalPrice,
      };
    }
    case "get_apppointment_value":
      return {
        ...state,
        appointmentValue: computeBookingPriceDollars(state).appointmentValue,
      };
    case "set_flexible_time":
      const currentSubTotal = Number(state.subTotal);
      return {
        ...state,
        isFlexible: !state.isFlexible,
        cleanTime: "",
        // Same reason as `set_time`: Flexible Time and Same-Day are mutually
        // exclusive and the engine throws on a booking claiming both, so taking
        // one clears the other here rather than relying on the step to hide it.
        ...CLEARED_SAME_DAY,
        subTotal: !state.isFlexible
          ? currentSubTotal - 10
          : currentSubTotal + 10,
        discount: !state.isFlexible ? state.discount + 10 : state.discount - 10,
      };
    case "set_date":
      /**
       * Changing the date clears the Same-Day choice. Whether Same-Day is offered
       * is a property of a specific date, so carrying the previous answer across a
       * date change would let a customer keep a surcharge, or a window, belonging
       * to a day they are no longer booking.
       */
      return {
        ...state,
        cleanDate: action.payload,
        ...CLEARED_SAME_DAY,
      };
    case "set_time":
      /**
       * Picking a clock time drops Same-Day, the mirror of `set_same_day`
       * clearing `cleanTime` below.
       *
       * It used to clear only `isFlexible`, which was safe only because the step
       * hid the time grid outright whenever Same-Day was on — the exclusivity
       * lived in a `sameDay ? null :` in the JSX rather than in the state. That
       * made the grid vanish and reappear under the customer, and it meant any
       * second surface reaching this action could build the both-set booking the
       * pricing engine throws on. Enforced here instead, so the two choices are
       * exclusive because the state says so.
       */
      return {
        ...state,
        isFlexible: false,
        cleanTime: action.payload,
        ...CLEARED_SAME_DAY,
      };
    case "set_same_day":
      /**
       * Same-Day and Flexible Time are mutually exclusive (see `CONTEXT.md`), and
       * the engine throws on a booking claiming both — so taking one clears the
       * other here rather than letting an impossible pair reach pricing.
       */
      return {
        ...state,
        sameDay: true,
        sameDayMode: true,
        isFlexible: false,
        cleanTime: "",
        /**
         * One Time here too, for the same reason `set_time` clears Same-Day here
         * rather than trusting the step to hide the grid: the rule belongs in the
         * state, not in whichever component happened to dispatch. This action
         * sets `sameDayMode` itself, so it can be the customer's first entry into
         * the mode, and a booking that reached it that way would otherwise carry
         * a plan and a same-day surcharge together.
         */
        frequencyBeforeSameDay:
          state.sameDayMode ? state.frequencyBeforeSameDay : state.occurance,
        occurance: 0,
        arrivalWindow: action.payload.arrivalWindow,
        arrivalWindowLabel: action.payload.arrivalWindowLabel ?? "",
        sameDaySurchargePercent: action.payload.surchargePercent ?? 0,
      };
    case "clear_same_day_selection":
      /**
       * Drop the chosen window but stay in Same-Day.
       *
       * Used when the window a customer picked closes underneath them, which the
       * step's polling makes visible. Staying in the mode is the point: there may
       * well be a later window still open, and dropping them back on the calendar
       * would answer a question they did not ask.
       */
      return {
        ...state,
        ...CLEARED_SELECTION,
      };
    case "enter_same_day":
      /**
       * Flexible Time goes off on the way in. The engine throws outright on a
       * booking that is both, and leaving the app's default `isFlexible: true`
       * standing would mean every price shown inside this mode — including the
       * surcharge preview on the windows themselves — is computed from a
       * combination that cannot be booked.
       */
      return {
        ...state,
        sameDayMode: true,
        cleanDate: action.payload,
        cleanTime: "",
        isFlexible: false,
        /**
         * Same-Day is a one-off job, so the plan goes to One Time on the way in.
         *
         * A subscription is a standing weekly slot; "today, in the next three
         * hours" is the opposite of one, and the two combined would bill every
         * later cycle at today's Same-Day surcharge for a cleaning nobody asked
         * to be same-day. The frequency row is hidden inside the mode rather
         * than merely defaulted, so this is the only value it can hold.
         *
         * Held, not discarded: `exit_same_day` puts it back.
         */
        frequencyBeforeSameDay:
          state.sameDayMode ? state.frequencyBeforeSameDay : state.occurance,
        occurance: 0,
        ...CLEARED_SELECTION,
      };
    case "exit_same_day":
      /**
       * Back to the step's own defaults, date included — see `exitSameDay`.
       *
       * The date has to go. Today is the one date the ordinary controls cannot
       * correctly offer times for, which is why the calendar's floor is tomorrow;
       * leaving it selected would hand the customer a regular flexible booking
       * for today, promising a 9am-4pm arrival at whatever hour it already is.
       * They land on the calendar with nothing chosen, the same state a customer
       * reaching this step for the first time sees.
       *
       * The flexible window comes back on because that is what a booking starts
       * as, so the only thing left to choose is the date.
       */
      return {
        ...state,
        ...CLEARED_SAME_DAY,
        cleanDate: "",
        cleanTime: "",
        isFlexible: true,
        /**
         * And the plan comes back with it. `?? state.occurance` covers the case
         * where nothing was remembered — a reload straight into the mode — by
         * leaving the current value alone rather than inventing a default.
         */
        occurance: state.frequencyBeforeSameDay ?? state.occurance,
        frequencyBeforeSameDay: null,
      };
    case "set_occurance":
      return {
        ...state,
        occurance: action.payload,
      };
    case "set_address":
      return {
        ...state,
        address: action.payload,
      };
    case "set_zip":
      return {
        ...state,
        zip: action.payload,
      };
    case "set_zip_editor_open":
      return {
        ...state,
        zipEditorOpen: Boolean(action.payload),
      };
    case "set_proximity":
      return {
        ...state,
        proximityTier: action.payload.tier,
        proximityPercent: action.payload.percent,
        proximityToken: action.payload.token,
        proximityFor: action.payload.key,
        proximityPending: Boolean(action.payload.pending),
      };
    case "set_price_notice":
      return {
        ...state,
        priceNotice: action.payload,
      };
    case "set_appartment":
      return {
        ...state,
        appartmentNo: action.payload,
      };
    case "set_addon":
      return {
        ...state,
        addOns: {
          type: action.payload as string[],
          // The server applies the same rule from the same module, so the
          // add-on cost shown here is the add-on cost charged. This branch
          // previously priced laundry at the windows rate and lumped every
          // other add-on into the oven rate.
          cost: addOnsCostDollars(state.types, action.payload as string[]),
        },
      };
    case "set_getintype":
      return {
        ...state,
        getInType: action.payload,
      };
    case "set_pets":
      return {
        ...state,
        pets: action.payload,
      };
    case "set_instructions":
      return {
        ...state,
        instructions: action.payload,
      };
    case "set_promocode":
      return {
        ...state,
        code: action.payload.code,
        codeDiscount: action.payload.codeDiscount,
      };
    case "set_tip":
      return {
        ...state,
        tip: !isNaN(action.payload) ? action.payload : 0,
      };
    case "set_payment_intent":
      return {
        ...state,
        paymentIntent: action.payload,
      };
    case "set_task_payment_intent":
      return {
        ...state,
        taskPaymentIntent: action.payload,
      };
    case "set_token":
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};
